import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInventory } from '../context/InventoryContext';

const { FiArrowRight, FiChevronRight } = FiIcons;

const Models = () => {
  const { getModelData } = useInventory();
  const models = getModelData();
  
  // Group models by category
  const categories = models.reduce((acc, model) => {
    if (!acc[model.category]) {
      acc[model.category] = [];
    }
    acc[model.category].push(model);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-black text-white">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1617469767053-d3b16ee6a4ab?w=1920&h=600&fit=crop&crop=center" 
            alt="Audi lineup" 
            className="w-full h-full object-cover opacity-60"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Audi Models
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Explore our lineup of premium vehicles combining innovative technology, sophisticated design, and exhilarating performance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="bg-gray-100 py-6 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {Object.keys(categories).map((category) => (
              <a 
                key={category} 
                href={`#${category.toLowerCase()}`}
                className="px-4 py-2 rounded-md bg-white shadow-sm text-gray-900 font-medium hover:bg-red-600 hover:text-white transition-colors"
              >
                {category}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Model Categories */}
      {Object.entries(categories).map(([category, categoryModels], categoryIndex) => (
        <section 
          key={category}
          id={category.toLowerCase()}
          className={`py-16 ${categoryIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {category} Models
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {category === 'SUV' && 'Versatile luxury SUVs combining spaciousness, capability, and premium comfort.'}
                {category === 'Sedan' && 'Sophisticated sedans offering elegant design, advanced technology, and dynamic performance.'}
                {category === 'Electric' && 'Innovative electric vehicles delivering sustainable luxury without compromise.'}
                {category === 'Sportback' && 'Dynamic sportbacks blending four-door practicality with coupe-inspired styling.'}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {categoryModels.map((model, index) => (
                <motion.div
                  key={model.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-200"
                >
                  <div className="relative">
                    <img 
                      src={model.image} 
                      alt={`Audi ${model.name}`} 
                      className="w-full h-60 object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Audi {model.name}
                    </h3>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-lg font-bold text-red-600">
                        Starting at ${model.startingPrice.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4 line-clamp-2">{model.description}</p>
                    
                    <div className="space-y-2 mb-6">
                      {model.highlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{highlight.title}</span>
                          <span className="font-semibold">{highlight.value}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Link
                      to={`/models/${model.id}`}
                      className="flex items-center justify-between w-full bg-black text-white py-3 px-4 rounded-md font-medium hover:bg-gray-900 transition-colors"
                    >
                      <span>Learn More</span>
                      <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Compare Models */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Find Your Perfect Audi
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Not sure which model is right for you? Compare features, specifications, and pricing to find your ideal match.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-lg shadow-lg"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Model Comparison Tool
                </h3>
                <p className="text-gray-600 mb-6">
                  Compare up to three Audi models side by side. View specifications, features, performance data, and more to make an informed decision.
                </p>
                <Link
                  to="/compare"
                  className="bg-red-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2"
                >
                  <span>Compare Models</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-gray-100 p-4 rounded-lg">
                  <div className="h-20 flex items-center justify-center text-center">
                    <span className="text-gray-500 font-medium">Select Model 1</span>
                  </div>
                </div>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <div className="h-20 flex items-center justify-center text-center">
                    <span className="text-gray-500 font-medium">Select Model 2</span>
                  </div>
                </div>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <div className="h-20 flex items-center justify-center text-center">
                    <span className="text-gray-500 font-medium">Select Model 3</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Experience Audi in Person
              </h2>
              <p className="text-lg text-gray-300 mb-8">
                Visit our Halifax showroom to see our full lineup of Audi vehicles. Schedule a test drive to experience the performance, luxury, and technology firsthand.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/inventory"
                  className="bg-red-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-red-700 transition-colors duration-200"
                >
                  View Inventory
                </Link>
                <Link
                  to="/contact"
                  className="border border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white hover:text-black transition-colors duration-200"
                >
                  Schedule Test Drive
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=800" 
                alt="Audi showroom" 
                className="rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Models;